/**
 * Подвижное окно карточки — общий вид для «Заказов», «Врачей» и «Клиник».
 *
 * Карточки этих трёх разделов раньше вели себя по-разному: заказ открывался
 * панелью у правого края, врач и клиника — модальным окном по центру, с
 * затемнением на весь экран. Разница ничем не оправдана: это одно и то же
 * действие — «открыть запись, посмотреть и поправить», — и владелец попросил
 * свести их к одному виду.
 *
 * Что это даёт, кроме единообразия:
 *
 *   • список ПОД окном остаётся видимым и рабочим: затемнения нет, можно
 *     листать журнал или плитки, не закрывая карточку;
 *   • окно ТАСКАЮТ за шапку — если оно закрыло нужную строку, его отодвигают,
 *     а не закрывают и открывают заново;
 *   • ширину тянут за левый край, двойной щелчок по краю возвращает исходную;
 *   • место и ширина запоминаются у каждого вида карточек СВОИ: карточка
 *     заказа шире карточки врача, и сводить их к одной ширине незачем.
 *
 * Высота — по содержимому, но не выше экрана: пустых полос внутри окна нет, а
 * если полей больше, чем влезает, прокручиваются они, а не страница.
 *
 * На ТЕЛЕФОНЕ таскать окно некуда: там карточка разворачивается на весь экран,
 * страница позади придерживается, а «назад» закрывает карточку.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useBodyScrollLock } from './useBodyScrollLock.js';
import { useCloseOnBack } from './useCloseOnBack.js';

/** Где лежит окно, какой оно ширины и высоты. */
export interface Окно {
  x: number;
  y: number;
  w: number;
  /**
   * Заданная высота. `undefined` — по содержимому (обычное состояние).
   *
   * Тянут её за нижний край. Нужна там, где содержимое длиннее экрана и
   * прокручивается внутри: в карточке счёта это целый бланк документа, и
   * окно хочется то растянуть под него, то ужать, чтобы видеть список.
   */
  h?: number;
}

/** Зазор до краёв экрана — чтобы окно не липло к ним вплотную. */
export const ОТСТУП = 16;

/** Уже этого окно не бывает: поля в строках по два перестают помещаться. */
const МИН = 420;

/** Ниже этого окно бессмысленно: остаются шапка да полоса кнопок. */
const МИН_ВЫСОТА = 220;

/**
 * С этой ширины поля карточки встают в ДВА СТОЛБЦА.
 *
 * Столбцу нужно около 420 точек: на меньшем «Выбрать способ оплаты» и
 * названия конструкций начинают переноситься по слогам.
 */
export const ШИРИНА_ДВА = 880;

/** Окно целиком на экране и не уже (не ниже) допустимого. */
export function зажать(о: Окно): Окно {
  const предел = Math.max(МИН, window.innerWidth - 2 * ОТСТУП);
  const w = Math.min(предел, Math.max(МИН, Math.round(о.w)));
  const x = Math.min(Math.max(ОТСТУП, Math.round(о.x)), Math.max(ОТСТУП, window.innerWidth - w - ОТСТУП));
  /* По высоте оставляем видимой хотя бы шапку: за неё окно и вернут. */
  const y = Math.min(Math.max(ОТСТУП, Math.round(о.y)), Math.max(ОТСТУП, window.innerHeight - 80));
  /*
    Высота не должна выводить низ окна за край экрана: утащив его туда,
    вернуть край мышью было бы нечем. `undefined` оставляем как есть —
    это «по содержимому».
  */
  const потолок = Math.max(МИН_ВЫСОТА, window.innerHeight - y - ОТСТУП);
  const h = о.h === undefined ? undefined : Math.min(потолок, Math.max(МИН_ВЫСОТА, Math.round(о.h)));
  return { x, y, w, h };
}

/** Окно по середине экрана — обычное место для карточки записи. */
function поСередине(ширина: number): Окно {
  const w = Math.min(ширина, window.innerWidth - 2 * ОТСТУП);
  return зажать({ x: Math.round((window.innerWidth - w) / 2), y: ОТСТУП * 2, w });
}

type Частично = Partial<Окно>;

function типичное(о: Частично): о is Окно {
  return typeof о.x === 'number' && typeof о.y === 'number' && typeof о.w === 'number' && о.w > 0;
}

export function CardWindow({
  ключ,
  title,
  subtitle,
  onClose,
  footer,
  ширина = 760,
  поУмолчанию,
  children,
}: {
  /** Где запоминается место окна. У каждого вида карточек свой. */
  ключ: string;
  title: string;
  /** Строка под названием: номер счёта, состояние записи. */
  subtitle?: ReactNode;
  onClose: () => void;
  /** Полоса кнопок внизу окна. Не прокручивается вместе с полями. */
  footer?: ReactNode;
  /** Ширина при первом открытии, если своё место ещё не задано. */
  ширина?: number;
  /** Своё место при первом открытии — когда его считают по странице. */
  поУмолчанию?: () => Окно;
  /** Поля карточки. Функции передаётся нынешняя ширина окна. */
  children: ReactNode | ((ширина: number) => ReactNode);
}) {
  /*
   * На ТЕЛЕФОНЕ делить экран не с чем: карточка занимает его целиком.
   */
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const sync = () => setWide(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  const начальное = () => {
    try {
      const сырое = window.localStorage.getItem(ключ);
      if (сырое) {
        const о = JSON.parse(сырое) as Частично;
        if (типичное(о)) return зажать(о);
      }
    } catch {
      // Приватный режим или мусор в хранилище — просто встанем по умолчанию.
    }
    return поУмолчанию ? поУмолчанию() : поСередине(ширина);
  };
  const [окно, setОкно] = useState<Окно>(начальное);

  useEffect(() => {
    try {
      window.localStorage.setItem(ключ, JSON.stringify(окно));
    } catch {
      // Место окна не сохранилось — ронять из-за этого карточку незачем.
    }
  }, [ключ, окно]);

  /* Свернули браузер — окно не должно остаться за краем экрана. */
  useEffect(() => {
    const сверить = () => setОкно((о) => зажать(о));
    window.addEventListener('resize', сверить);
    return () => window.removeEventListener('resize', сверить);
  }, []);

  // Прокрутку страницы держим только на телефоне, где карточка закрывает всё.
  useBodyScrollLock(!wide);

  /*
   * «Назад» на телефоне закрывает КАРТОЧКУ.
   *
   * Карточка не меняет адреса страницы, и для браузера её открытие не событие.
   * Прежде «назад» уходил на шаг до программы — то есть закрывал её целиком,
   * вместе с открытой карточкой и несохранёнными правками.
   */
  useCloseOnBack(true, onClose);

  /**
   * Перетаскивание за шапку.
   *
   * Хватать можно за любое её место, кроме крестика: иначе окно уезжало бы
   * при попытке его закрыть.
   */
  const тащить = (event: React.PointerEvent) => {
    if (!wide || (event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    const отступX = event.clientX - окно.x;
    const отступY = event.clientY - окно.y;
    const move = (e: PointerEvent) =>
      setОкно((о) => зажать({ ...о, x: e.clientX - отступX, y: e.clientY - отступY }));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.removeProperty('user-select');
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    // Без этого протяжка выделяет текст карточки, как мышью по абзацу.
    document.body.style.userSelect = 'none';
  };

  /*
   * Полям сообщается ДЕЙСТВИТЕЛЬНАЯ ширина.
   *
   * На телефоне окно развёрнуто во весь экран, а запомненная ширина осталась
   * от компьютера — по ней поля встали бы в два столбца на 390 точках.
   */
  const поля =
    typeof children === 'function' ? children(wide ? окно.w : window.innerWidth) : children;

  return (
    <div
      className={`otc-card-window fixed z-40 flex ${wide ? '' : 'inset-0'}`}
      style={
        wide
          ? {
              left: окно.x,
              top: окно.y,
              width: окно.w,
              /* Заданная высота главнее: без неё окно растёт по содержимому
                 и упирается в край экрана, как было всегда. */
              height: окно.h,
              maxHeight: `calc(100vh - ${окно.y + ОТСТУП}px)`,
            }
          : undefined
      }
    >
      {wide && (
        <РучкаШирины
          окно={окно}
          onОкно={setОкно}
          исходное={() => (поУмолчанию ? поУмолчанию() : поСередине(ширина))}
        />
      )}
      <div
        className={`flex min-w-0 flex-1 flex-col overflow-hidden bg-bg ${wide ? 'rounded-xl' : ''}`}
        // Тень отделяет окно от списка: затемнения позади нет.
        style={{ boxShadow: '0 18px 50px rgba(28,27,24,0.28)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Шапка — она же ручка, за которую окно таскают. */}
        <div
          onPointerDown={тащить}
          className={`flex flex-shrink-0 items-start justify-between gap-3 px-6 pb-3 pt-6 ${
            wide ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
        >
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-ink2">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="otc-btn otc-btn-ghost otc-btn-icon" aria-label="Закрыть">
            <X size={18} className="text-ink2" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{поля}</div>

        {/*
          Подвал — ОТДЕЛЬНАЯ полоса, а не кнопки, брошенные под содержимым:
          он отделён линией и заливкой и не уезжает вместе с полями.
        */}
        {footer && (
          <div
            className="flex flex-shrink-0 flex-wrap items-center gap-2 border-t px-6 py-3.5"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            {footer}
          </div>
        )}

        {/*
          Нижний край, за который тянут высоту. Лежит ПОВЕРХ подвала, у самой
          кромки окна: так его находят мышью независимо от того, есть подвал
          или нет.
        */}
        {wide && (
          <РучкаВысоты окно={окно} onОкно={setОкно} исходное={() => ({ ...окно, h: undefined })} />
        )}
      </div>
    </div>
  );
}

/**
 * Левый край окна, за который тянут ширину.
 *
 * Окно растёт ВЛЕВО: правый край при протяжке остаётся на месте. Двойной
 * щелчок возвращает исходную ширину — утащив край к упору, вернуть его мышью
 * обратно неудобно.
 */
function РучкаШирины({
  окно,
  onОкно,
  исходное,
}: {
  окно: Окно;
  onОкно: (о: Окно) => void;
  исходное: () => Окно;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      title="Потяните, чтобы изменить ширину окна"
      onDoubleClick={() => onОкно(исходное())}
      onPointerDown={(event) => {
        event.preventDefault();
        const startX = event.clientX;
        const правый = окно.x + окно.w;
        const move = (e: PointerEvent) => {
          const w = окно.w + (startX - e.clientX);
          onОкно(зажать({ y: окно.y, w, x: правый - Math.max(МИН, w) }));
        };
        const up = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          document.body.style.removeProperty('user-select');
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        // Без этого протяжка выделяет текст карточки, как мышью по абзацу.
        document.body.style.userSelect = 'none';
      }}
      /* Полоса лежит ПОВЕРХ левого края окна: иначе половину её закрывал бы
         сам лист, и мышь попадала бы в него, а не в ручку. */
      className="relative z-10 -mr-3 w-3 flex-shrink-0 cursor-col-resize self-stretch rounded-l-xl hover:bg-black/5"
    />
  );
}

/**
 * Нижний край окна, за который тянут высоту.
 *
 * Окно растёт ВНИЗ: верх при протяжке остаётся на месте, чтобы шапка не
 * убегала из-под мыши. Двойной щелчок возвращает высоту по содержимому —
 * то же самое, что у левого края с шириной.
 *
 * Высота нужна не всякой карточке, но мешать она не мешает: пока за край
 * не потянули, окно ведёт себя как раньше — растёт по содержимому.
 */
function РучкаВысоты({
  окно,
  onОкно,
  исходное,
}: {
  окно: Окно;
  onОкно: (о: Окно) => void;
  исходное: () => Окно;
}) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      title="Потяните, чтобы изменить высоту окна"
      onDoubleClick={() => onОкно(зажать(исходное()))}
      onPointerDown={(event) => {
        event.preventDefault();
        const startY = event.clientY;
        /* Отсчитываем от нынешней высоты — даже если она была по содержимому. */
        const начальная =
          окно.h ?? (event.currentTarget.parentElement?.getBoundingClientRect().height ?? МИН_ВЫСОТА);
        const move = (e: PointerEvent) =>
          onОкно(зажать({ ...окно, h: начальная + (e.clientY - startY) }));
        const up = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          document.body.style.removeProperty('user-select');
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        document.body.style.userSelect = 'none';
      }}
      className="absolute inset-x-0 bottom-0 z-10 h-3 cursor-row-resize rounded-b-xl hover:bg-black/5"
    />
  );
}
